import { Turbo } from '@hotwired/turbo-rails';

/**
 * Custom Turbo Stream action: `<turbo-stream action="reload_frame" target="...">`.
 * Calls `frame.reload()` on the targeted <turbo-frame>, with no template payload.
 * I know this is a big comment, but the history here is important.
 *
 * Why this exists
 * ---------------
 * The dashboard `update` controller responds to form submissions with a turbo
 * stream that triggers a morph-reload of the dashboard frame. None of the
 * stream actions turbo-rails ships with work for this case, because of how
 * Turbo's renderers interact with `data-turbo-permanent` elements — which the
 * engine relies on for chart canvases (Highcharts holds a reference to the
 * original DOM node as its `renderTo`) and for modals (browser top-layer
 * state is tied to the original element).
 *
 * Built-in `replace`/`update` with `method: :morph` puts the dashboard HTML
 * inside the stream's <template>. Stream messages are processed by a renderer
 * that, before applying actions, walks any permanent elements present in both
 * the document and the incoming template and swaps them out for clones of
 * themselves. The original DOM nodes get detached. For charts that means
 * Highcharts ends up rendering into a node that's no longer in the visible
 * tree; for modals it means the dialog loses its top-layer state. The stream
 * "works" in the sense that no error is thrown — but the page is silently
 * broken.
 *
 * The frame and page morph renderers don't run that clone-swap step, so
 * routing the response through one of those renderers preserves permanent
 * elements correctly. The frame morph renderer is only used when a frame
 * `reload()` is called explicitly — form submissions inside a frame go
 * through the regular replace renderer, even with `refresh="morph"` set on
 * the frame. So we have to reach the morph path by issuing `frame.reload()`
 * ourselves.
 *
 * The built-in `refresh` action almost gets us there, but it's page-level:
 * it refreshes the document the stream landed in. In the iframe path that's
 * the dashboard's own URL and it works. In the host-page Turbo Frame embed
 * path the document is the host page, so `refresh` would reload the host
 * instead of the dashboard frame.
 *
 * What this action does
 * ---------------------
 * The stream payload is empty, so the document/template scan finds no
 * permanent elements to swap and the destructive code path doesn't fire.
 * The action then calls `frame.reload()` on the targeted frame, which (when
 * the frame has both `src` and `refresh="morph"`) re-fetches the src and
 * routes the response through the frame morph renderer. That renderer
 * preserves `data-turbo-permanent` elements via Idiomorph's normal skip
 * behavior, without any element-identity gymnastics — Highcharts' `renderTo`
 * stays valid and modals keep their top-layer state across morphs.
 *
 * If turbo-rails ever ships a built-in frame-targeted reload stream action,
 * this file becomes unnecessary.
 */
Turbo.StreamActions.reload_frame = function reloadFrame() {
  const target = document.getElementById(this.target);
  if (target?.reload) target.reload();
};
